import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Search, MapPin, AlertCircle } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";

export default function SheltersDirectory() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [region, setRegion] = useState("");
    const [killOnly, setKillOnly] = useState(false);
    useEffect(() => { api.get("/shelters").then((r) => setItems(r.data)); }, []);
    const regions = useMemo(() => Array.from(new Set(items.map((i) => i.region).filter(Boolean))).sort(), [items]);
    const filt = useMemo(() => items.filter((i) => {
        if (killOnly && !i.kill_shelter) return false;
        if (region && i.region !== region) return false;
        if (q && !`${i.name} ${i.location}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    }), [items, q, killOnly, region]);

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="shelters-directory">
            <div className="mb-8">
                <div className="eyebrow mb-3">Shelters · Worldwide</div>
                <h1 className="font-serif text-4xl lg:text-6xl tracking-tight">Where the intake happens.</h1>
                <p className="mt-3 text-[var(--rl-text-2)] max-w-2xl">Every private, county, and municipal shelter feeding into the RescueLink network — from Seoul to Sydney.</p>
            </div>
            <div className="flex flex-wrap gap-4 items-center mb-4">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rl-text-3)]" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shelters…"
                           className="pl-9 border-[var(--rl-border)] h-11 rounded-full" data-testid="shelters-search" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="shelters-kill-filter">
                    <Checkbox checked={killOnly} onCheckedChange={setKillOnly} /> High-euthanasia only
                </label>
            </div>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="shelters-region-filter">
                <button onClick={() => setRegion("")} className={`px-4 py-1.5 rounded-full text-sm border ${region === "" ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]" : "border-[var(--rl-border)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"}`}>All regions</button>
                {regions.map((r) => (
                    <button key={r} onClick={() => setRegion(r)}
                            className={`px-4 py-1.5 rounded-full text-sm border ${region === r ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]" : "border-[var(--rl-border)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"}`}>{r}</button>
                ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filt.map((s) => {
                    const pct = Math.min(100, Math.round((s.current_intake / Math.max(1, s.capacity)) * 100));
                    const over = pct >= 90;
                    return (
                        <div key={s.id} className="card-warm rounded-xl p-5 flex gap-5" data-testid={`shelter-card-${s.id}`}>
                            <div className="w-24 h-24 rounded-lg overflow-hidden shrink-0 bg-[var(--rl-cream)]">
                                <img src={s.logo_url} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-serif text-xl leading-tight">{s.name}</h3>
                                    {s.kill_shelter && <Badge className="bg-[var(--rl-urgent)] hover:bg-[var(--rl-urgent)] text-white text-[10px] uppercase tracking-widest whitespace-nowrap"><AlertCircle className="w-3 h-3 mr-1" /> High-risk</Badge>}
                                </div>
                                <Badge variant="outline" className="mt-1 text-[10px] uppercase tracking-widest">{s.type}</Badge>
                                <div className="flex items-center gap-1 text-xs text-[var(--rl-text-3)] mt-1"><MapPin className="w-3 h-3" /> {s.location} <span className="ml-1 text-[10px] uppercase tracking-widest bg-[var(--rl-cream)] px-2 py-0.5 rounded-full">{s.region}</span></div>
                                <p className="text-sm text-[var(--rl-text-2)] mt-2 line-clamp-2">{s.description}</p>
                                <div className="mt-3">
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="text-[var(--rl-text-2)]">Capacity</span>
                                        <span className={over ? "text-[var(--rl-urgent)] font-semibold" : ""}>{s.current_intake} / {s.capacity} ({pct}%)</span>
                                    </div>
                                    <Progress value={pct} className={`h-1.5 ${over ? "[&>div]:bg-[var(--rl-urgent)]" : ""}`} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
